/** Sprint 5 · Form Builder · 表單分派（grant 管理）+ 開放維護 / 關閉維護 + 所屬人員設定。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Chip, IconButton, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import {
  getForm, listGrants, addGrant, removeGrant, publishForm, closeForm,
  Grant, Capability, FormDetail,
} from './services/formBuilderService';

const FUNC = 'form_manager';
const CAPS: Capability[] = ['create', 'submit_review', 'review', 'publish_close',
  'fill', 'edit_own', 'view_all', 'delete'];

const FormGrantsPage: React.FC = () => {
  const { id } = useParams();
  const fid = Number(id);
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`formBuilder.${k}`, d);
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();

  const [form, setForm] = useState<FormDetail | null>(null);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [ng, setNg] = useState<{ principal_type: 'role' | 'user'; principal_id: string; capability: Capability }>(
    { principal_type: 'user', principal_id: '', capability: 'fill' });
  const [targets, setTargets] = useState('');

  const load = useCallback(async () => {
    try {
      const f = await getForm(fid); setForm(f);
      setTargets((f.target_user_ids || []).join(','));
      setGrants((await listGrants(fid)).items);
    } catch (err) { showApiError(err); }
  }, [fid, showApiError]);

  useEffect(() => { if (!permLoading && hasPermission(FUNC, 'read')) load(); }, [permLoading, hasPermission, load]);

  const parseTargets = () => targets.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);

  const doPublish = async () => {
    try { const f = await publishForm(fid, parseTargets()); setForm(f); showSuccess('SYS420001'); }
    catch (err) { showApiError(err); }
  };
  const doClose = async () => {
    try { const f = await closeForm(fid); setForm(f); showSuccess('SYS420001'); } catch (err) { showApiError(err); }
  };
  const add = async () => {
    const pid = Number(ng.principal_id);
    if (!Number.isFinite(pid) || pid <= 0) return;
    try {
      await addGrant(fid, { principal_type: ng.principal_type, principal_id: pid, capability: ng.capability });
      showSuccess('SYS420001'); setNg({ ...ng, principal_id: '' }); setGrants((await listGrants(fid)).items);
    } catch (err) { showApiError(err); }
  };
  const del = async (gid: number) => {
    try { await removeGrant(fid, gid); setGrants((await listGrants(fid)).items); } catch (err) { showApiError(err); }
  };

  if (permLoading) return null;
  if (!hasPermission(FUNC, 'read')) return <Box sx={{ p: 3 }}>{t('common.noPermission', '您沒有權限')}</Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Button size="small" onClick={() => nav('/form_manager')}>← {tt('back', '返回列表')}</Button>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 1 }}>
        <Typography variant="h6">{form?.title || tt('grants', '分派')}</Typography>
        {form && <Chip size="small" label={tt(`st_${form.status}`, form.status)} />}
      </Stack>

      {/* 開放 / 關閉 + 所屬人員 */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('distribution', '開放維護')}</Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField size="small" label={tt('targetIds', '所屬人員 user id（逗號分隔）')} value={targets}
            onChange={(e) => setTargets(e.target.value)} sx={{ minWidth: 280 }} />
          <Button variant="contained" onClick={doPublish}
            disabled={!(form && (form.status === 'approved' || form.status === 'closed'))}>
            {tt('publish', '開放維護')}</Button>
          <Button variant="outlined" color="warning" onClick={doClose}
            disabled={!(form && form.status === 'published')}>{tt('close', '關閉維護')}</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {tt('publishHint', 'approved 或 closed 可開放；published 可關閉。所屬人員即可填寫者。')}</Typography>
      </Paper>

      {/* grant 表 */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" gutterBottom>{tt('grantTable', '權限分派')}</Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
          <TextField size="small" select label={tt('principalType', '對象')} value={ng.principal_type}
            onChange={(e) => setNg({ ...ng, principal_type: e.target.value as 'role' | 'user' })} sx={{ width: 110 }}>
            <MenuItem value="user">{tt('p_user', '使用者')}</MenuItem>
            <MenuItem value="role">{tt('p_role', '角色')}</MenuItem>
          </TextField>
          <TextField size="small" type="number" label="ID" value={ng.principal_id}
            onChange={(e) => setNg({ ...ng, principal_id: e.target.value })} sx={{ width: 100 }} />
          <TextField size="small" select label={tt('capability', '能力')} value={ng.capability}
            onChange={(e) => setNg({ ...ng, capability: e.target.value as Capability })} sx={{ width: 160 }}>
            {CAPS.map((cp) => <MenuItem key={cp} value={cp}>{cp}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={add}>{tt('add', '新增')}</Button>
        </Stack>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{tt('principalType', '對象')}</TableCell><TableCell>ID</TableCell>
            <TableCell>{tt('capability', '能力')}</TableCell><TableCell align="right" /></TableRow></TableHead>
          <TableBody>
            {grants.length === 0 && <TableRow><TableCell colSpan={4}>
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>{tt('noGrants', '尚無分派')}</Typography>
            </TableCell></TableRow>}
            {grants.map((g) => (
              <TableRow key={g.id} hover>
                <TableCell>{g.principal_type === 'user' ? tt('p_user', '使用者') : tt('p_role', '角色')}</TableCell>
                <TableCell>{g.principal_id}</TableCell>
                <TableCell>{g.capability}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" color="error" onClick={() => del(g.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default FormGrantsPage;
